import React, { useState, useEffect } from "react";
import { useConfig } from "@/lib/config";
import { MapPin, KeyRound, ExternalLink, AlertCircle } from "lucide-react";

export default function StreetView({ lat, lng, address }) {
  const { google_maps_key } = useConfig();
  const [hasImagery, setHasImagery] = useState(null); // null=checking, true=yes, false=no

  useEffect(() => {
    if (!google_maps_key || !lat || !lng) { setHasImagery(null); return; }
    // Use Street View Static API metadata endpoint to check if imagery exists nearby
    const url = `https://maps.googleapis.com/maps/api/streetview/metadata?location=${lat},${lng}&radius=500&source=outdoor&key=${google_maps_key}`;
    fetch(url).then(r => r.json()).then(d => setHasImagery(d.status === "OK")).catch(() => setHasImagery(false));
  }, [lat, lng, google_maps_key]);

  if (!google_maps_key) {
    return (
      <div className="rounded-lg border border-dashed border-[#33291f] bg-[#1c1712] p-6 flex flex-col items-center text-center gap-2" data-testid="street-view-disabled" style={{ height: 280 }}>
        <div className="w-12 h-12 rounded-full bg-[#241d16] border border-[#4a3a2b] flex items-center justify-center">
          <KeyRound size={20} className="text-[#d4a574]" />
        </div>
        <div className="font-display text-lg font-semibold">Street View</div>
        <div className="text-[12px] text-[#b8ac96] max-w-sm">Add a Google Maps API key in <span className="font-mono text-[#d4a574]">/app/backend/.env</span> as <span className="font-mono">GOOGLE_MAPS_API_KEY</span> to enable Street View.</div>
        <div className="text-[11px] font-mono text-[#7a6d5a] flex items-center gap-1"><MapPin size={11} /> {address || `${lat}, ${lng}`}</div>
      </div>
    );
  }

  const gmapsUrl = `https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${lat},${lng}`;

  if (hasImagery === false) {
    return (
      <div className="rounded-lg border border-[#33291f] bg-[#1c1712] p-6 flex flex-col items-center text-center gap-3" data-testid="street-view-no-coverage" style={{ height: 280 }}>
        <div className="w-12 h-12 rounded-full bg-[#241d16] border border-[#fbbf24]/40 flex items-center justify-center">
          <AlertCircle size={20} className="text-[#fbbf24]" />
        </div>
        <div className="font-display text-lg font-semibold">No Street View coverage here</div>
        <div className="text-[12px] text-[#b8ac96] max-w-sm">Google doesn't have Street View imagery within 500 m of this location — common for private property, industrial areas, and rural roads.</div>
        <a href={gmapsUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-[12px] text-[#d4a574] hover:text-[#e8b988] font-semibold"><ExternalLink size={12} /> Explore on Google Maps</a>
        <div className="text-[11px] font-mono text-[#7a6d5a] flex items-center gap-1"><MapPin size={11} /> {address || `${lat}, ${lng}`}</div>
      </div>
    );
  }

  const src = `https://www.google.com/maps/embed/v1/streetview?key=${google_maps_key}&location=${lat},${lng}&heading=210&pitch=10&fov=90`;
  if (hasImagery === null) {
    return (
      <div className="rounded-lg border border-[#33291f] bg-[#1c1712] flex items-center justify-center" data-testid="street-view-loading" style={{ height: 280 }}>
        <div className="flex items-center gap-2 text-[12px] text-[#7a6d5a]">
          <div className="w-2 h-2 rounded-full bg-[#d4a574] animate-pulse" />
          Checking Street View coverage…
        </div>
      </div>
    );
  }
  return (
    <div className="rounded-lg overflow-hidden border border-[#33291f] relative" style={{ height: 280 }} data-testid="street-view">
      <iframe title="Street View" src={src} width="100%" height="100%" style={{ border: 0 }} loading="lazy" allowFullScreen />
      <a href={gmapsUrl} target="_blank" rel="noreferrer" className="absolute bottom-2 left-2 inline-flex items-center gap-1 px-2 py-1 rounded bg-[#14100c]/85 border border-[#33291f] text-[10px] text-[#d4a574] hover:text-[#e8b988]"><ExternalLink size={10} /> Open in Google Maps</a>
    </div>
  );
}
